import { useEffect } from "react";
import { AppState, Platform } from "react-native";
import { defineEvent, track } from "../analytics";
import { appLifecycle } from "../state";

/** `cold_start` is true for the first open of the process, false when returning from the background. */
const AppOpened = defineEvent<{ cold_start: boolean }>("App Opened");
const AppBackgrounded = defineEvent("App Backgrounded");

/**
 * Sends `App Opened` on cold start and on each return to the foreground, and
 * `App Backgrounded` when the app goes to the background. Uses `AppState` on
 * native and the page's visibility on web. Call once, in the root layout.
 */
export function useAppLifecycleAnalytics(): void {
  useEffect(() => (Platform.OS === "web" ? watchPageVisibility() : watchAppState()), []);
}

interface Lifecycle {
  foreground(): void;
  background(): void;
}

/**
 * Tracks each change between foreground and background once per process, even
 * if the root layout remounts. An app launched in the background (e.g. by a
 * push notification) sends its cold-start `App Opened` when it first comes to
 * the foreground.
 */
/**
 * Where the app is when the hook mounts. `neither` (iOS `inactive`, or a state
 * not reported yet) changes nothing: on a remount, the app is still wherever
 * it was.
 */
type StartState = "foreground" | "background" | "neither";

function lifecycle(start: StartState): Lifecycle {
  const app = appLifecycle();
  const foreground = (): void => {
    if (app.inForeground) {
      return;
    }
    app.inForeground = true;
    track(AppOpened, { cold_start: !app.opened });
    app.opened = true;
  };
  const background = (): void => {
    if (!app.inForeground) {
      return;
    }
    app.inForeground = false;
    track(AppBackgrounded);
  };
  if (start === "foreground") {
    foreground();
  } else if (start === "background") {
    background();
  }
  return { foreground, background };
}

/**
 * Only `active` is the foreground. At start, `unknown` (state not reported
 * yet), `inactive` or `background` is not an open: the cold-start `App Opened`
 * waits for the first `active`. Only `background` at start counts as being in
 * the background, so a remount while `inactive` sends nothing. After start, `inactive` (iOS only: app
 * switcher, Control Centre, an incoming call) is neither: the app is still on
 * screen and has not been left.
 */
function watchAppState(): () => void {
  const { currentState } = AppState;
  const app = lifecycle(
    currentState === "active" ? "foreground"
    : currentState === "background" ? "background"
    : "neither",
  );
  const subscription = AppState.addEventListener("change", (state) => {
    if (state === "active") {
      app.foreground();
    } else if (state === "background") {
      app.background();
    }
  });
  return () => {
    subscription.remove();
  };
}

/** A hidden page (another tab, a minimised window) counts as backgrounded. */
function watchPageVisibility(): () => void {
  const app = lifecycle(document.visibilityState === "visible" ? "foreground" : "background");
  const onChange = (): void => {
    if (document.visibilityState === "visible") {
      app.foreground();
    } else {
      app.background();
    }
  };
  document.addEventListener("visibilitychange", onChange);
  return () => {
    document.removeEventListener("visibilitychange", onChange);
  };
}
